/*
 * The Belgian Brewer: table booking form (sections/tbb-table-booking.liquid).
 * - Offers only dates and times the venue is open: arrival times run every 30 minutes from opening
 *   until the latest start, and a table must end by closing time (so a 4-hour table starts earlier).
 * - Groups at or above the deposit size see a tick box for the deposit; bigger groups than the form
 *   takes are pointed to private hire.
 * - Sends the request to Make and shows its answer: a thank-you with a reference, or what to change.
 *   When the time is full, Make sends back times that still have room, shown as buttons.
 * The form is only a guide; Make checks every rule again before anything is saved.
 */
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const pad = (n) => String(n).padStart(2, '0');
const toMins = (s) => {
  const m = /^([0-9]{1,2}):([0-9]{2})$/.exec(String(s || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};
const hm = (m) => pad(Math.floor(m / 60)) + ':' + pad(m % 60);
const ymd = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
const money = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

function setUp(root) {
  if (root.dataset.tbbReady) return;
  root.dataset.tbbReady = 'true';
  const form = root.querySelector('[data-tbb-table-form]');
  const configEl = root.querySelector('[data-tbb-table-config]');
  if (!form || !configEl) return;
  let cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }
  const T = cfg.text || {};
  const hours = cfg.hours || {};
  const latest = Number.isFinite(toMins(cfg.latest)) ? toMins(cfg.latest) : 20 * 60;
  const closed = String(cfg.closedDates || '').match(/[0-9]{4}-[0-9]{2}-[0-9]{2}/g) || [];
  const q = (sel) => root.querySelector(sel);
  const guests = q('[data-tbb-guests]');
  const date = q('[data-tbb-date]');
  const time = q('[data-tbb-time]');
  const lengths = [...root.querySelectorAll('[data-tbb-length]')];
  const depositWrap = q('[data-tbb-deposit]');
  const depositBox = q('[data-tbb-deposit-box]');
  const depositText = q('[data-tbb-deposit-text]');
  const hire = q('[data-tbb-hire]');
  const status = q('[data-tbb-status]');
  const timesWrap = q('[data-tbb-times]');
  const submit = q('[data-tbb-submit]');
  const done = q('[data-tbb-done]');

  const len = () => Number((lengths.find((r) => r.checked) || {}).value || 2);
  const earliest = () => new Date(Date.now() + Number(cfg.noticeHours || 24) * 3600000);

  // Date limits: from the first day the notice allows, up to the bookable days ahead
  const first = earliest();
  const last = new Date();
  last.setDate(last.getDate() + Number(cfg.daysAhead || 90));
  date.min = ymd(first);
  date.max = ymd(last);

  const errorFor = (field) => document.getElementById(field.getAttribute('aria-describedby'));
  const setError = (field, text) => {
    if (!field) return;
    field.setAttribute('aria-invalid', text ? 'true' : 'false');
    const el = errorFor(field);
    if (el) el.textContent = text || '';
  };
  const clearStatus = () => {
    status.hidden = true;
    status.textContent = '';
    status.classList.remove('is-error');
    timesWrap.hidden = true;
    timesWrap.replaceChildren();
  };

  const dayHours = (value) => {
    if (!value || closed.includes(value)) return null;
    const d = new Date(value + 'T12:00:00');
    const h = hours[DAYS[d.getDay()]] || {};
    const open = toMins(h.open);
    const close = toMins(h.close);
    return Number.isFinite(open) && Number.isFinite(close) ? { open, close } : null;
  };

  // Arrival times for the chosen date and length
  const fillTimes = () => {
    const wanted = time.value;
    const options = [];
    let message = '';
    if (date.value) {
      const h = dayHours(date.value);
      if (!h) message = T.closedDay;
      else {
        const lastStart = Math.min(latest, h.close - len() * 60);
        const soonest = earliest();
        for (let m = h.open; m <= lastStart; m += 30) {
          const at = new Date(date.value + 'T' + hm(m) + ':00');
          if (at >= soonest) options.push(hm(m));
        }
        if (!options.length) message = T.noTimes;
      }
    }
    const prompt = new Option(!date.value ? T.choose : options.length ? T.pickTime : T.none, '');
    time.replaceChildren(prompt, ...options.map((t) => new Option(t, t)));
    time.disabled = !options.length;
    if (options.includes(wanted)) time.value = wanted;
    setError(date, message);
  };

  // Deposit tick box and the bigger-group note
  const syncGuests = () => {
    const more = guests.value === 'more';
    hire.hidden = !more;
    submit.disabled = more;
    const n = Number(guests.value);
    const needs = !more && n >= Number(cfg.depositFrom || 7);
    depositWrap.hidden = !needs;
    depositBox.required = needs;
    if (needs) {
      const pp = Number(cfg.depositPP || 10);
      depositText.textContent = String(T.depositBox || '')
        .replace('[amount]', money(pp * n))
        .replace('[people]', n)
        .replace('[pp]', money(pp));
    } else {
      depositBox.checked = false;
      setError(depositBox, '');
    }
  };

  guests.addEventListener('change', () => {
    syncGuests();
    clearStatus();
  });
  date.addEventListener('change', () => {
    fillTimes();
    clearStatus();
  });
  lengths.forEach((r) => r.addEventListener('change', () => {
    fillTimes();
    clearStatus();
  }));
  time.addEventListener('change', () => {
    setError(time, '');
    clearStatus();
  });
  form.querySelectorAll('input, textarea').forEach((el) => el.addEventListener('input', () => setError(el, '')));
  depositBox.addEventListener('change', () => setError(depositBox, ''));

  const validate = () => {
    let firstBad = null;
    const check = (field, ok, text) => {
      setError(field, ok ? '' : text);
      if (!ok && !firstBad) firstBad = field;
    };
    check(date, !!date.value && !!dayHours(date.value), date.value ? T.closedDay : T.required);
    check(time, !!time.value, T.required);
    const name = form.elements.name;
    const email = form.elements.email;
    const phone = form.elements.phone;
    check(name, name.value.trim().length >= 2, T.required);
    check(phone, (phone.value.match(/[0-9]/g) || []).length >= 9, T.badPhone);
    check(email, /^[^ @]+@[^ @]+[.][^ @]{2,}$/.test(email.value.trim()), T.badEmail);
    if (!depositWrap.hidden) check(depositBox, depositBox.checked, T.depositRequired || T.required);
    return firstBad;
  };

  const showTimes = (times) => {
    timesWrap.replaceChildren();
    if (!times || !times.length) return;
    const label = document.createElement('p');
    label.textContent = T.otherTimes;
    timesWrap.append(label);
    times.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'tbb-table__time';
      b.textContent = t;
      b.addEventListener('click', () => {
        if (![...time.options].some((o) => o.value === t)) time.append(new Option(t, t));
        time.disabled = false;
        time.value = t;
        clearStatus();
        submit.focus();
      });
      timesWrap.append(b);
    });
    timesWrap.hidden = false;
  };

  const fieldFor = (name) => ({ date, time, hours: lengths[0], guests, deposit: depositBox }[name] || form.elements[name]);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearStatus();
    const bad = validate();
    if (bad) {
      bad.focus();
      return;
    }
    submit.disabled = true;
    submit.textContent = T.sending;
    let answer = null;
    try {
      const res = await fetch(cfg.endpoint, { method: 'POST', body: new URLSearchParams(new FormData(form)) });
      answer = await res.json();
    } catch (e) {
      answer = null;
    }
    submit.disabled = false;
    submit.textContent = T.submit;
    if (!answer || typeof answer.message !== 'string') {
      status.textContent = String(T.failed || '').replace('[phone]', cfg.phone || '');
      status.classList.add('is-error');
      status.hidden = false;
      return;
    }
    if (answer.ok) {
      form.hidden = true;
      done.querySelector('[data-tbb-done-text]').textContent = answer.message;
      const ref = done.querySelector('[data-tbb-done-ref]');
      ref.textContent = answer.ref ? T.ref + ' ' + answer.ref : '';
      ref.hidden = !answer.ref;
      done.hidden = false;
      done.scrollIntoView({ block: 'center', behavior: 'smooth' });
      done.focus({ preventScroll: true });
      return;
    }
    status.textContent = answer.message;
    status.classList.add('is-error');
    status.hidden = false;
    showTimes(answer.times);
    if (answer.hire) {
      hire.hidden = false;
    }
    const field = fieldFor(answer.field);
    if (field && field !== time) setError(field, answer.message);
    status.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });

  syncGuests();
  fillTimes();
}

const init = (root = document) => root.querySelectorAll('[data-tbb-table]').forEach(setUp);
init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
